<template>
  <sidebar-layout
    :show-leaderboard-ad="loading === 0"
  >
    <loading-spinner v-if="loading !== 0" />
    <div v-else-if="user" class="author-index" data-test-id="author-index">
      <h1 class="author-index__username" data-test-id="author-index-username"> {{ user.username }} </h1>
      <user-avatar
        class="author-index__avatar user-avatar--medium"
        :user="user"
        size="full"
        data-test-id="author-index-avatar"
      />
      <div
        class="author-index__description" data-test-id="author-index-description"
        v-html="user.description"
      />
      <paginated-list
        :items="posts"
        :current-page="page"
        :total-count="totalCount"
        :per-page="perPage"
      />
    </div>
    <template v-slot:sidebar-ads>
      <sidebar-ads v-if="loading === 0" :sections="sidebarAdSections" />
    </template>
  </sidebar-layout>
</template>

<script>
import SidebarAds from '@/components/Ads/SidebarAds.vue';
import UserAvatar from '@/components/User/UserAvatar.vue';
import PaginatedList from '@/components/Pagination/PaginatedList.vue';
import { config } from '@/configuration/nexus-config';
import { SEARCH_QUERY } from '@/graphql/queries/search-queries';
import { GET_USER_SUMMARY_QUERY } from '@/graphql/queries/user-queries';
import { authorCollectionJsonLd } from '@/utils/structured-data';

export default {
  name: 'AuthorIndex',
  components: {
    SidebarAds,
    UserAvatar,
    PaginatedList,
  },
  data() {
    return {
      loading: 0,
      posts: [],
      user: {},
      dataLayer: {},
      perPage: 20,
      totalCount: 0,
      slug: this.$route.params.slug,
      sidebarAdSections: config.sidebarAdSections,
    };
  },
  computed: {
    page() {
      if (this.$route.query.page) {
        return parseInt(this.$route.query.page, 10);
      }
      return 1;
    },
  },
  watch: {
    user(to) {
      if (!to) { return; }
      this.onLoad();
    },
  },
  methods: {
    setJsonLd() {
      this.$store.dispatch(
        'setContentJsonLd',
        authorCollectionJsonLd(
          this.user,
          this.totalCount,
          this.$route.path,
          this.$root.hostname,
          this.$site.settings.condition_name,
        ),
      );
    },
    onLoad() {
      this.setJsonLd();

      this.$store.dispatch('updateGlobalContext', {
        search: {
          type: 'Author Index',
          query: this.posts.map((p) => p.id),
        },
        page: {
          id: this.user.id,
        },
      });
      this.$emit('loaded', this.dataLayer);
    },
  },
  metaInfo() {
    const metaObject = {
      meta: [
        {
          name: 'robots',
          vmid: 'robots',
          content: 'noindex, follow',
        },
      ],
      link: [
        { vmid: 'canonical', content: null },
      ],
    };
    if (!this.loading && this.user) {
      metaObject.title = `Author: ${this.user.username}`;
    }
    return metaObject;
  },
  apollo: {
    search: {
      query: SEARCH_QUERY,
      variables() {
        return {
          page: this.page,
          query: '*',
          postTypes: ['cas', 'post', 'story', 'thread', 'recipe', 'sponsored_article', 'quiz', 'graphic', 'gallery'],
          searchType: 'POST',
          searchSort: 'DATE',
          authorSlug: this.slug,
          limit: this.perPage,
        };
      },
      result({ data }) {
        if (data) {
          if (!process.browser) {
          // make sure that we set the jsonLD
          // for the page during SSR
            this.setJsonLd();
          }
          if (!data.search || !data.search.searchResults) { return; }
          this.dataLayer = data.search.dataLayer;
          this.totalCount = data.search.total;
          this.posts = data.search.searchResults;
        } else {
          this.posts = [];
        }
      },
    },
    user: {
      query: GET_USER_SUMMARY_QUERY,
      variables() {
        return {
          slug: this.slug.toLowerCase(),
        };
      },
      result({ data }) {
        if (data) {
          if (!process.browser) {
          // make sure that we set the jsonLD
          // for the page during SSR
            this.setJsonLd();
          }
          if (!data.user) { return; }

          const { user: { slug } } = data;
          let permapath = `/author/${slug}`;

          if (slug && this.$route.path !== permapath) {
            const queryString = this.$route.fullPath.split('?')[1];
            if (queryString) {
              permapath = `${permapath}?${queryString}`;
              this.$router.replace(permapath);
              return;
            }
            this.$router.replace(`${permapath}${this.$route.hash}`);
          }

          this.user = data.user;
        } else {
          this.user = {};
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_author-index';
</style>
